// Painel "Organizar com IA" (espec. §10): pede um rascunho de organização em lote na
// montagem, mostra as famílias sugeridas NA ORDEM que o servidor mandou (já por
// previsto decrescente — a tela não reordena) e confirma a seleção numa ÚNICA
// chamada. Proposta apenas: nada é gravado até o clique em "Confirmar selecionadas",
// e essa é a ÚNICA escrita (`confirmOrganizeWithAi`), levando o par
// `draftId`/`expectedVersion` que o servidor devolveu — um conflito de versão (409
// `ai_draft_version_conflict`) mostra a frase pronta e não tenta de novo sozinho.
//
// Padrão de diálogo herdado do `GroupEditorOffcanvas`/`RuleEditorOffcanvas`:
// `role="dialog"`, foco que entra ao montar, Escape fecha.
import { useEffect, useRef, useState } from "react";

import { describeProfile } from "../profile";
import {
  confirmOrganizeWithAi,
  newIdempotencyKey,
  organizeWithAi,
} from "./disbursementAiApi";
import { money } from "../format";

const CONFIDENCE_LABEL = Object.freeze({
  high: "alta",
  medium: "média",
  low: "baixa",
});

const CAVEAT_LABEL = Object.freeze({
  share_approximation: "Fatia por insumo é aproximada",
  sem_historico: "Sem histórico de pagamentos",
  single_work: "Histórico de uma obra só",
  sparse_months: "Pagamentos muito espalhados",
  truncated: "Lista de insumos truncada",
  low_volume: "Poucos pagamentos observados",
  market_default: "Padrão de mercado, não do histórico",
});

const REASON_LABEL = Object.freeze({
  no_candidates: "Não há grupos ou insumos sem regra para organizar.",
});

// A cota da IA é da obra e é compartilhada: com ela esgotada nenhuma proposta nova é
// pedida. Um pedido já em andamento (`pendingCreates`) continua consultável abaixo.
export const QUOTA_EXHAUSTED_TEXT =
  "A cota de uso da IA desta obra foi utilizada neste período. Não é possível pedir uma nova proposta agora.";

/**
 * Folha da linha de família: `money()` lê `useProjectContext` por dentro, então fica
 * SEMPRE no topo do corpo desta folha — nunca dentro do `.map` do painel, onde a
 * contagem de hooks mudaria conforme o número de famílias do rascunho.
 */
const FamilyRow = ({ family, rejected, onToggle }) => {
  const plannedText = money(family.plannedCents);
  const memberCount = family.members?.length ?? 0;
  const caveats = family.caveats ?? [];
  return (
    <li
      className="disbursement-organize-panel__family"
      data-testid="organize-family"
    >
      <h3>{family.name}</h3>
      <p>
        {`${memberCount} ${
          memberCount === 1 ? "insumo" : "insumos"
        } · previsto ${plannedText}`}
      </p>
      <p>{describeProfile(family.suggestedProfile)}</p>
      <p>
        <span>Confiança: </span>
        <span>{CONFIDENCE_LABEL[family.confidence] ?? "baixa"}</span>
      </p>
      {caveats.length > 0 ? (
        <ul className="disbursement-organize-panel__caveats">
          {caveats.map((caveat) => (
            <li key={caveat}>
              {CAVEAT_LABEL[caveat] ?? "Ressalva registrada"}
            </li>
          ))}
        </ul>
      ) : null}
      <button
        type="button"
        onClick={() => onToggle(family.name)}
        aria-pressed={rejected}
      >
        {rejected ? "Reincluir" : "Descartar"}
      </button>
    </li>
  );
};

const pendingCreates = new Map();
export const discardPendingOrganizeRequest = (workId) =>
  pendingCreates.delete(workId);

const OrganizeWithAiPanel = ({
  workId,
  onApplied,
  onClose,
  quotaExhausted = false,
}) => {
  const panelRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [reason, setReason] = useState(null);
  const [rejected, setRejected] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [processing, setProcessing] = useState(false);
  const createController = useRef(null);
  const confirmRequest = useRef(null);
  const receiptRef = useRef(null);
  const currentWork = useRef(workId);
  const workGeneration = useRef(0);
  currentWork.current = workId;

  const loadCreate = (fresh = false) => {
    if (fresh || !pendingCreates.has(workId))
      pendingCreates.set(workId, newIdempotencyKey());
    const key = pendingCreates.get(workId);
    const generation = workGeneration.current;
    const controller = new AbortController();
    createController.current?.abort();
    createController.current = controller;
    setLoading(true);
    setError(null);
    setReason(null);
    setProcessing(false);
    organizeWithAi(workId, { idempotencyKey: key, signal: controller.signal })
      .then((result) => {
        if (controller.signal.aborted || workGeneration.current !== generation)
          return;
        if (result?.status === "processing") {
          setProcessing(true);
          return;
        }
        if (pendingCreates.get(workId) === key) pendingCreates.delete(workId);
        if (result?.status === "cannot_propose")
          setReason(result.reason ?? "no_candidates");
        else setDraft(result?.draft ?? null);
      })
      .catch((requestError) => {
        if (!controller.signal.aborted && workGeneration.current === generation)
          setError(
            requestError?.message ?? "A IA está temporariamente indisponível.",
          );
      })
      .finally(() => {
        if (!controller.signal.aborted && workGeneration.current === generation)
          setLoading(false);
      });
  };

  useEffect(() => {
    workGeneration.current += 1;
    currentWork.current = workId;
    setDraft(null);
    setReason(null);
    setRejected(new Set());
    setError(null);
    setConfirming(false);
    confirmRequest.current = null;
    receiptRef.current = null;
    if (pendingCreates.has(workId)) {
      setLoading(false);
      setProcessing(true);
    } else if (quotaExhausted) {
      // Sem cota não se abre proposta nova: nada é pedido ao servidor.
      setLoading(false);
    } else loadCreate();
    return () => {
      workGeneration.current += 1;
      currentWork.current = null;
      createController.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workId]);

  // Escape fecha o painel; foco entra no diálogo assim que ele monta.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKeyDown);
    panelRef.current?.focus();
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const families = draft?.payload?.families ?? [];
  const accepted = families
    .map((family) => family.name)
    .filter((name) => !rejected.has(name));

  const toggle = (name) => {
    if (confirming || receiptRef.current) return;
    setRejected((current) => {
      const next = new Set(current);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const confirm = async () => {
    if (loading || confirming || accepted.length === 0 || !draft) return;
    setConfirming(true);
    setError(null);
    const body = {
      expectedVersion: draft.version,
      acceptedFamilies: [...accepted].sort(),
    };
    const generation = workGeneration.current;
    const fingerprint = JSON.stringify([workId, draft.draftId, body]);
    if (confirmRequest.current?.fingerprint !== fingerprint) {
      confirmRequest.current = {
        fingerprint,
        idempotencyKey: newIdempotencyKey(),
      };
    }
    try {
      const result =
        receiptRef.current ??
        (await confirmOrganizeWithAi(workId, draft.draftId, body, {
          idempotencyKey: confirmRequest.current.idempotencyKey,
        }));
      if (
        currentWork.current !== workId ||
        workGeneration.current !== generation
      )
        return;
      if (
        result?.status !== "executed" ||
        result.draftId !== draft.draftId ||
        result.version !== draft.version + 1 ||
        !Number.isSafeInteger(result.rulesRevision) ||
        result.rulesRevision < 0 ||
        !Array.isArray(result.appliedFamilies) ||
        JSON.stringify([...result.appliedFamilies].sort()) !==
          JSON.stringify(body.acceptedFamilies)
      ) {
        throw new Error(
          "A confirmação não retornou um recibo válido. Consulte novamente.",
        );
      }
      receiptRef.current = result;
      await onApplied?.({
        ...result,
        code: result.regeneration?.status === "ready" ? 200 : 202,
      });
    } catch (requestError) {
      if (
        currentWork.current === workId &&
        workGeneration.current === generation
      )
        setError(
          requestError?.message ?? "Não foi possível aplicar as famílias.",
        );
    } finally {
      if (
        currentWork.current === workId &&
        workGeneration.current === generation
      )
        setConfirming(false);
    }
  };

  return (
    <div className="offcanvas active">
      <section
        className="offcanvas-container disbursement-organize-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Organizar com IA"
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="offcanvas-header">
          <h2>Organizar com IA</h2>
          <button type="button" onClick={() => onClose?.()}>
            Cancelar
          </button>
        </header>

        <div className="offcanvas-content">
          {quotaExhausted && !pendingCreates.has(workId) && !draft ? (
            <p role="status" data-testid="organize-quota-exhausted">
              {QUOTA_EXHAUSTED_TEXT}
            </p>
          ) : null}
          {loading ? <p>Montando as famílias…</p> : null}
          {reason ? (
            <p>
              {REASON_LABEL[reason] ?? "Não foi possível montar as famílias."}
            </p>
          ) : null}
          {error ? <p role="alert">{error}</p> : null}
          {processing ? (
            <p role="status">
              O pedido está em processamento ou aguarda recuperação. Consulte o
              mesmo pedido para continuar.
            </p>
          ) : null}
          {!loading && pendingCreates.has(workId) ? (
            <div>
              <button type="button" onClick={() => loadCreate()}>
                Consultar pedido
              </button>
              <button type="button" onClick={() => loadCreate(true)}>
                Iniciar novo pedido
              </button>
            </div>
          ) : null}

          {families.length > 0 ? (
            <>
              <div className="disbursement-organize-panel__actions">
                <button
                  type="button"
                  disabled={confirming || Boolean(receiptRef.current)}
                  onClick={() => setRejected(new Set())}
                >
                  Aceitar todas
                </button>
              </div>
              <ul className="disbursement-organize-panel__list">
                {families.map((family) => (
                  <FamilyRow
                    key={family.name}
                    family={family}
                    rejected={rejected.has(family.name)}
                    onToggle={toggle}
                  />
                ))}
              </ul>
              <footer>
                <button
                  type="button"
                  onClick={confirm}
                  disabled={confirming || accepted.length === 0}
                >
                  Confirmar selecionadas
                </button>
              </footer>
            </>
          ) : null}
        </div>
      </section>
    </div>
  );
};

export default OrganizeWithAiPanel;
